import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useBridgeState } from '@digitaz/bridge-sdk-client';
import * as S from './Bookmarks.styled';
import { Tabs } from '@/components/UI/Tabs';
import { TabBar } from '@/components/TabBar/TabBar';
import { InfiniteLoader } from '@/components/InfiniteLoader/InfiniteLoader';
import {
  OrderOption,
  OrderOptions,
  BookmarksGrid,
  SelectionHeader,
  BookmarkFilters,
  CollectionMasterModal,
  CollectionMasterSheet,
  GET_USER_FAVORITES,
} from '@/features/BookmarkCollections';
import { useBanner } from '@/hooks/useBanner';
import { useToastStore } from '@/store/useToastStore';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { Header } from '@/components/Header';
import { Footer, DesktopFooter } from '@/components/Footer';
import { mergeBookmarksResults } from '@/utils/helpers';
import { BOOKMARKS_CHUNK_SIZE } from '@/constants/common';
import { GET_USER_BOOKMARKS } from '@/api/queries/user.queries';
import { GetUserBookmarksResponse } from '@/api/types/user.types';
import { useUserFavorites } from '@/features/BookmarkCollections/hooks';
import { TABS_IDS } from '@/features/BookmarkCollections/constants';
import { useBookmarkSelectionStore } from '@/features/BookmarkCollections/store';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { PageTopControls } from '@/components/PageTopControls';
import { AppPromotionModal } from '@/features/BookmarkCollections/components/AppPromotionModal';
import { AdTransitionSource } from '@/api/types/ads.types';
import { BULK_REMOVE_BOOKMARKS } from '@/features/BookmarkCollections/api/mutations';
import { AdStatus } from '@/types';
import { useIosNudgeScrollOffTop } from '@/hooks/useIosNudgeScrollOffTop';

export const BookmarksView = () => {
  const { t, i18n } = useTranslation();
  const { isWebView } = useBridgeState();
  const { isDesktop } = useBreakpoint();
  const { openToast } = useToastStore();
  const { topBanner } = useBanner({ isDesktop });
  const {
    setIsSelectionActive,
    isSelectionActive,
    selectedItems,
    clearSelection,
    toggleSelectedItem,
  } = useBookmarkSelectionStore();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [isCollectionMasterOpen, setIsCollectionMasterOpen] = useState(false);
  const [orderBy, setOrderBy] = useState<OrderOption>(
    OrderOptions.CREATED_AT_DESC
  );
  const [isAppPromotionPopupOpen, setIsAppPromotionPopupOpen] = useState(false);
  // Only used to fetch categories and prefetch data for collection master
  const {
    bookmarksTotalCount,
    bookmarkedCategories,
    loading: userLoading,
  } = useUserFavorites({ noCache: true });
  const iosNudgeScrollOffTop = useIosNudgeScrollOffTop();

  const [bulkRemoveBookmarks] = useMutation(BULK_REMOVE_BOOKMARKS, {
    refetchQueries: [GET_USER_FAVORITES],
  });

  const {
    data,
    fetchMore,
    loading,
    refetch: refetchBookmarks,
  } = useQuery<GetUserBookmarksResponse>(GET_USER_BOOKMARKS, {
    variables: {
      first: BOOKMARKS_CHUNK_SIZE,
      orderType: orderBy || undefined,
      filters: activeCategory ? { categoryId: activeCategory } : undefined,
    },
  });

  const isRu = i18n.language === 'ru';
  const bookmarks = data?.currentUser?.bookmarks?.nodes ?? [];
  const hasBookmarks = bookmarks.length > 0 || !!bookmarksTotalCount;
  const hasSelectButton = hasBookmarks && !isSelectionActive;
  const { endCursor, hasNextPage } =
    data?.currentUser?.bookmarks?.pageInfo ?? {};

  const activeSelectedItems = useMemo(() => {
    if (!selectedItems.length) return [];

    const selected = new Set(selectedItems);
    const inactiveAdStatuses = new Set<AdStatus>([
      AdStatus.Expired,
      AdStatus.Archived,
    ]);
    return bookmarks
      .filter(
        ({ legacyResourceId, status }) =>
          selected.has(String(legacyResourceId)) &&
          !inactiveAdStatuses.has(status)
      )
      .map(({ legacyResourceId }) => String(legacyResourceId));
  }, [bookmarks, selectedItems]);

  function onLoadMore() {
    if (loading || !hasNextPage || !bookmarks.length) return;

    fetchMore({
      variables: { after: endCursor },
      updateQuery: (prev, { fetchMoreResult }) =>
        mergeBookmarksResults(prev, fetchMoreResult),
    });
  }

  const handleRemoveFromBookmarks = async () => {
    await bulkRemoveBookmarks({
      variables: {
        adIds: [],
        adLegacyIds: selectedItems,
      },
    });

    clearSelection();
    setIsSelectionActive(false);

    await refetchBookmarks({
      first: BOOKMARKS_CHUNK_SIZE,
      orderType: orderBy || undefined,
      filters: activeCategory ? { categoryId: activeCategory } : undefined,
    });

    openToast({
      text:
        selectedItems.length > 1
          ? t('bookmark_collections.selected_bookmarks_removed')
          : t('bookmark_collections.selected_bookmark_removed'),
    });
  };

  const handleSuccessAddToCollection = () => {
    setIsSelectionActive(false);
    setIsCollectionMasterOpen(false);
  };

  const handleStartSelection = () => {
    setIsSelectionActive(true);
    clearSelection();
  };

  const handleOrderChange = (order: OrderOption) => {
    setOrderBy(order);
    refetchBookmarks({
      first: BOOKMARKS_CHUNK_SIZE,
      orderType: order || undefined,
      filters: activeCategory ? { categoryId: activeCategory } : undefined,
    });
  };

  const handleCategoryChange = (categoryId: string | null) => {
    setActiveCategory(categoryId);
    refetchBookmarks({
      first: BOOKMARKS_CHUNK_SIZE,
      filters: categoryId ? { categoryId } : undefined,
    });
  };

  const handleOpenCollectionMaster = () => {
    iosNudgeScrollOffTop();
    setIsCollectionMasterOpen(true);
  };

  useEffect(() => {
    return () => {
      setIsSelectionActive(false);
      clearSelection();
    };
  }, [clearSelection, setIsSelectionActive]);

  const desktopSelectButton = useMemo(() => {
    if (userLoading) {
      return <SkeletonLoader borderRadius={14} width={117} height={44} />;
    }

    if (hasSelectButton) {
      return (
        <S.SelectButton
          onClick={handleStartSelection}
          data-testid="select-bookmarks-button"
        >
          {t('bookmark_collections.select_items')}
        </S.SelectButton>
      );
    }

    return null;
  }, [userLoading, hasSelectButton]);

  const mobileSelectButton = useMemo(() => {
    if (userLoading) {
      return (
        <SkeletonLoader borderRadius={9} width={isRu ? 95 : 62} height={30} />
      );
    }

    if (hasSelectButton) {
      return (
        <S.MobileSelectButton
          onClick={handleStartSelection}
          data-testid="select-bookmarks-button"
        >
          {t('bookmark_collections.select_items')}
        </S.MobileSelectButton>
      );
    }
  }, [userLoading, hasSelectButton]);

  return (
    <>
      <S.GlobalStyles />
      {!isWebView && topBanner}

      {!isDesktop && isSelectionActive ? null : (
        <PageTopControls>
          <Header
            rightAction={mobileSelectButton}
            title={t('bookmark_collections.bookmarks')}
          />
        </PageTopControls>
      )}

      <S.Page>
        <S.Bookmarks>
          <SelectionHeader
            title={t('bookmark_collections.bookmarks')}
            actions={{
              secondary: {
                title: t('bookmark_collections.selected_remove_from_bookmarks'),
                onClick: handleRemoveFromBookmarks,
                confirm: {
                  title: t(
                    'bookmark_collections.selected_remove_from_bookmarks_confirm_title'
                  ),
                  description: t(
                    'bookmark_collections.selected_remove_from_bookmarks_confirm_description'
                  ),
                },
              },
              primary: {
                priority: true,
                title: t('bookmark_collections.selected_add_to_collection'),
                onClick: handleOpenCollectionMaster,
              },
            }}
          />

          <S.TabsWrapper $isDisabled={isSelectionActive}>
            <Tabs
              hasPadding={false}
              activeTab={TABS_IDS.ADS}
              tabs={[
                {
                  active: true,
                  value: TABS_IDS.ADS,
                  label: t('pages.bookmarks.tabs.ads'),
                },
                {
                  active: false,
                  value: TABS_IDS.COLLECTIONS,
                  href: '/collections',
                  label: t('pages.bookmarks.tabs.collections'),
                },
                {
                  active: false,
                  value: TABS_IDS.SHOP_BOOKMARKS,
                  href: '/shop_bookmarks',
                  label: t('pages.bookmarks.tabs.shops'),
                },
                {
                  active: false,
                  value: TABS_IDS.SEARCH,
                  label: t('pages.bookmarks.tabs.search'),
                  onClick: () => setIsAppPromotionPopupOpen(true),
                  isButton: true,
                },
              ]}
            />

            {isDesktop && desktopSelectButton}
          </S.TabsWrapper>

          <BookmarkFilters
            orderBy={orderBy}
            loading={userLoading}
            categories={bookmarkedCategories}
            totalAdsCount={bookmarksTotalCount}
            onOrderChange={handleOrderChange}
            onCategoryChange={handleCategoryChange}
          />

          <>
            <BookmarksGrid
              bookmarks={bookmarks}
              loading={loading}
              hasBookmarks={hasBookmarks}
              selection={{
                isActive: isSelectionActive,
                selectedItems,
                onToggle: toggleSelectedItem,
              }}
              adTransitionSource={AdTransitionSource.Favorites}
            />
            {hasBookmarks && hasNextPage && (
              <InfiniteLoader onVisit={onLoadMore} />
            )}
          </>
        </S.Bookmarks>

        {isDesktop ? (
          <CollectionMasterModal
            syncOnSubmit
            bookmarks={activeSelectedItems}
            showLatestBookmark={false}
            isOpen={isCollectionMasterOpen}
            onClose={handleSuccessAddToCollection}
          />
        ) : (
          <CollectionMasterSheet
            syncOnSubmit
            bookmarks={activeSelectedItems}
            showLatestBookmark={false}
            isOpen={isCollectionMasterOpen}
            onClose={handleSuccessAddToCollection}
          />
        )}
      </S.Page>

      <AppPromotionModal
        isOpen={isAppPromotionPopupOpen}
        onClose={() => setIsAppPromotionPopupOpen(false)}
      />

      {isDesktop ? <DesktopFooter /> : <Footer />}
      {!isDesktop && <TabBar />}
    </>
  );
};
